import { GetStaticPaths, GetStaticProps, NextPage } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { MyHead } from '@/components/molecules/MyHead'
import { VerticalNavDefaultPageTemplate } from '@/components/templates/VerticalNavDefaultPageTemplate'
import { ProfileSingleColumnContentTemplate } from '@/components/templates/ProfileSingleColumnContentTemplate'
import { IAnswerBase, IQuestionBase, Language, QuestionTypeBase } from '@/types'
import { getStaticStores, useStores } from '@/utils'
import { QuestionBox } from '@/components/molecules/QuestionBox'
import { AnswerGroup } from '@/components/organisms/AnswerGroup'
import { ForumModal } from '@/components/organisms/ForumModal'
import { useEffect, useState } from 'react'
import { PollBox } from '@/components/organisms/PollBox'
import { Button } from '@/components/atoms/Button'
import { observer } from 'mobx-react'
import styles from './index.module.scss'

type Props = {
  question: IQuestionBase
  staticAnswers: IAnswerBase[]
  staticHasNextPage: boolean
}

const NUM_OF_ITEMS = 10

const Question: NextPage<Props> = observer(({ question, staticAnswers, staticHasNextPage }) => {
  const [targetQuestion, setTargetQuestion] = useState<IQuestionBase>(question)
  const { questions } = useStores()

  // ISRで取得した配列より大きい場合、ページ送りの初回リクエストが実行されていることにする
  const hasDoneLoadMore = questions.answers[question.slug]?.length > staticAnswers?.length

  const [isFetching, setIsFetching] = useState(false)

  const fetchAnswers = async () => {
    if (isFetching) {
      return
    }

    setIsFetching(true)
    await questions.fetchAnswers({
      slug: question.slug,
      limitOfAnswers: hasDoneLoadMore ? NUM_OF_ITEMS : NUM_OF_ITEMS * 2,
      shouldRefresh: !hasDoneLoadMore,
    })
    setIsFetching(false)
  }

  // ブラウザ側での初期化時に Store に hasNextPage を設定
  useEffect(() => {
    questions.updateHasNextAnswersPage(question.slug, staticHasNextPage)
  }, [])

  return (
    <VerticalNavDefaultPageTemplate>
      <MyHead
        title={question?.title}
        description='Contents of consultation'
        isTitleWithSuffix={false}
        image={question?.ogImage}
      />
      <ProfileSingleColumnContentTemplate>
        <h2 className={styles.heading}>Post</h2>
        <div className={styles.questionBox}>
          {question.questionType === QuestionTypeBase.QUESTION && <QuestionBox question={targetQuestion} />}
          {question.questionType === QuestionTypeBase.POLL && <PollBox question={targetQuestion} />}
        </div>
        <div className={styles.content}>
          <AnswerGroup items={hasDoneLoadMore ? questions.answers[question.slug] : staticAnswers} question={question} />
        </div>
        {questions.hasNextAnswersPage[question.slug] && (
          <div className={styles.button}>
            <Button
              styleType='default'
              componentType='button'
              buttonSize='large'
              isLoading={isFetching}
              onClick={() => fetchAnswers()}
            >
              + Load More
            </Button>
          </div>
        )}
      </ProfileSingleColumnContentTemplate>
      <ForumModal
        targetQuestion={question.questionType === QuestionTypeBase.QUESTION ? targetQuestion : null}
        setTargetQuestion={(targetQ: IQuestionBase) => setTargetQuestion(targetQ)}
      />
    </VerticalNavDefaultPageTemplate>
  )
})

export const getStaticProps: GetStaticProps = async ({ locale, params }) => {
  const { viewer, questions } = getStaticStores()
  await viewer.changeLanguage(locale as Language)
  const questionSlug = params.question_slug as string
  const question = await questions.fetchQuestion({ slug: questionSlug, limitOfAnswers: NUM_OF_ITEMS })

  if (!question) {
    return {
      notFound: true,
      revalidate: 1,
    }
  }

  return {
    props: {
      question,
      staticAnswers: questions.answers[questionSlug],
      staticHasNextPage: questions.hasNextAnswersPage[questionSlug],
      ...(await serverSideTranslations(locale, ['global', 'm', 'p', 'validation'])),
    },
    revalidate: 1,
  }
}

// eslint-disable-next-line @typescript-eslint/require-await
export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export default Question
